import { FileText } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * `apps/web-admin/src/routes/contracts/`: button labels and states quoted
 * verbatim from `contract-list-page.tsx`, `contract-conditions-tab.tsx`,
 * `contract-transition-buttons.tsx`, `contract-prices-tab.tsx`,
 * `bulk-price-update-modal.tsx` and `contract-document-tab.tsx`.
 * `requiredPermission` mirrors `contract.read` in `layout/navigation.ts`.
 */
export const CONTRACTS_MODULE: GuideModule = {
  id: 'contracts',
  title: 'Contratos',
  icon: FileText,
  requiredPermission: 'contract.read',
  priority: 4,
  summary:
    'Un contrato define las condiciones comerciales con una empresa: ciclo de facturación, plazo de pago, hora de corte, topes diarios de cantidad, días de servicio y la lista de precios. Los precios se cargan por tipo de servicio (un precio para almuerzo, otro para cena), no por plato: así no hace falta actualizar precios cada vez que rota el menú del día. Si un plato puntual necesita un precio distinto, se carga como excepción por plato, que gana sobre el precio por servicio mientras esté vigente. Cada contrato pertenece a una sola empresa, a un solo punto de venta y tiene un código único; una misma empresa puede tener un contrato en cada punto. Cuando editás un contrato activo, el sistema genera una nueva versión en vez de sobrescribir la anterior, así siempre queda un historial de qué cambió y cuándo. Los contratos pasan por tres transiciones posibles: activar, pausar y dar de baja, cada una con su propio permiso; activar exige que el contrato tenga al menos un precio por servicio vigente.',
  steps: [
    {
      title: 'Crear un contrato nuevo',
      description:
        'Andá a Contratos y hacé clic en "Nuevo contrato", o hacelo desde la pestaña "Contratos" del detalle de una empresa para que quede ya seleccionada. Elegí la empresa (si no vino preseleccionada), el "Punto de venta" desde el que se la atiende (el contrato queda atado a ese punto: sus pedidos, su producción y sus facturas siguen ese punto), cargá el código, la vigencia, el ciclo de facturación y las demás condiciones, y guardá.',
      keyTerms: ['contrato', 'vigencia', 'ciclo-de-facturacion'],
    },
    {
      title: 'Cargar el precio por servicio',
      description:
        'Entrá al contrato, andá a la pestaña "Lista de precios", sección "Precio por servicio", y hacé clic en "Agregar precio por tipo de servicio". Elegí el tipo de servicio (almuerzo o cena), el "Concepto de facturación" (viene preseleccionado el concepto por defecto; podés cambiarlo, por ejemplo para facturar la cena como servicio), cargá el precio, elegí la "Alícuota de IVA" de la lista (viene preseleccionada la que está por defecto, o la del concepto de facturación si tiene una) y la vigencia. Sin al menos un precio por servicio vigente, el contrato no se puede activar.',
      keyTerms: ['lista-de-precios', 'precio-por-servicio', 'alicuota-de-iva'],
    },
    {
      title: 'Cargar una excepción por plato',
      description:
        'En la misma pestaña, sección "Excepciones por plato", hacé clic en "Agregar precio". Elegí el menú puntual, cargá su precio propio, elegí la "Alícuota de IVA" de la lista (viene preseleccionada la que está por defecto) y la vigencia. Las alícuotas disponibles se administran en Configuración → "Alícuotas de IVA"; si la lista no carga, el formulario muestra un aviso con "Reintentar". Mientras esté vigente y disponible, ese precio gana sobre el precio por servicio para ese plato.',
      keyTerms: ['excepcion-por-plato'],
    },
    {
      title: 'Saber qué precio se usa en un pedido',
      description:
        'Cada línea de un pedido toma el primer precio que exista, en este orden: 1) el precio del contrato para ese plato (la excepción por plato), 2) el precio del contrato para el tipo de servicio del plato (almuerzo, cena), 3) el precio de catálogo del plato, que se usa como respaldo solo cuando el contrato no tiene ninguno de los dos anteriores y se factura con la alícuota de IVA por defecto de Configuración → "Alícuotas de IVA". Si el contrato marca ese plato o ese servicio como "no disponible", el plato no se puede pedir y no se usa el precio de catálogo. En la pestaña "Lista de precios" hay un recuadro "Qué precio se usa en cada línea del pedido" y un aviso con los servicios que hoy no tienen precio en el contrato y por lo tanto usan el precio de catálogo. En cada línea de un pedido, junto al precio unitario, se lee de dónde salió: "Contrato (menú)", "Contrato (servicio)" o "Catálogo".',
      keyTerms: ['precio-de-catalogo', 'precio-por-servicio', 'excepcion-por-plato'],
    },
    {
      title: 'Aplicar una actualización masiva de precios',
      description:
        'Desde "Lista de precios", hacé clic en "Actualización masiva", ingresá el porcentaje (positivo para aumentar, negativo para descontar) y hacé clic en "Previsualizar". Revisá la tabla de precios actuales contra los nuevos, con una fila por cada precio por servicio y por cada excepción por plato, y confirmá con "Confirmar y aplicar".',
      keyTerms: ['actualizacion-masiva'],
    },
    {
      title: 'Activar, pausar o dar de baja un contrato',
      description:
        'En la pestaña "Condiciones", usá los botones "Activar", "Pausar" o "Dar de baja" según corresponda. "Pausar" pide confirmación y "Dar de baja" pide un motivo. Cada botón está deshabilitado si no tenés el permiso o si el contrato no está en un estado que permita esa transición.',
      keyTerms: ['contrato-activo', 'contrato-pausado', 'baja'],
    },
    {
      title: 'Subir el documento del contrato',
      description:
        'En la pestaña "Documento", arrastrá el PDF a la zona indicada o hacé clic en "Seleccioná un archivo". Solo se aceptan PDF de hasta 5 MB.',
      keyTerms: ['documento-del-contrato'],
    },
    {
      title: 'Entender las versiones de un contrato',
      description:
        'La pestaña "Historial de versiones" lista todas las versiones de ese código de contrato, su estado y, si una versión venció, qué versión la reemplazó.',
      keyTerms: ['version-de-contrato'],
    },
    {
      title: 'Volver a la empresa desde el contrato',
      description:
        'En el detalle del contrato, el nombre de la empresa debajo del título es un enlace: hacé clic para volver directo a su detalle.',
      keyTerms: ['empresa'],
    },
  ],
  states: [
    {
      value: 'DRAFT',
      label: 'Borrador',
      description: 'El contrato todavía no fue activado. Se puede editar libremente.',
    },
    {
      value: 'ACTIVE',
      label: 'Activo',
      description: 'El contrato está vigente. Editarlo genera una nueva versión.',
    },
    {
      value: 'PAUSED',
      label: 'Pausado',
      description: 'El contrato está temporalmente sin efecto. Se puede reactivar.',
    },
    {
      value: 'EXPIRED',
      label: 'Vencido',
      description: 'El contrato llegó a su fecha de fin.',
    },
    {
      value: 'TERMINATED',
      label: 'Dado de baja',
      description: 'El contrato fue terminado de forma definitiva, con un motivo cargado.',
    },
  ],
  faq: [
    {
      question: '¿Por qué el botón "Activar" aparece deshabilitado?',
      answer:
        'Puede ser porque el contrato ya no está en un estado que permita activarse, o porque tu rol no tiene el permiso necesario. Pasá el mouse sobre el botón para ver el motivo.',
    },
    {
      question: '¿Qué pasa si edito un contrato que ya está activo?',
      answer:
        'El sistema genera una nueva versión con los cambios, en vez de modificar la versión vigente. Los precios por servicio y las excepciones por plato se copian a la nueva versión. Podés ver todas las versiones en "Historial de versiones".',
    },
    {
      question: '¿Qué precio se cobra si el contrato no tiene precio para un plato?',
      answer:
        'Se cobra el precio de catálogo del plato, con la alícuota de IVA por defecto. El orden completo es: excepción por plato, precio por servicio y, al final, precio de catálogo. La única excepción es un plato o servicio marcado como "no disponible" en el contrato: ese no se puede pedir y no vuelve al precio de catálogo.',
    },
    {
      question: '¿La actualización masiva de precios se aplica de una vez sin avisar?',
      answer:
        'No. Primero hacé clic en "Previsualizar" para ver la tabla de precios actuales contra los nuevos, y recién después confirmás con "Confirmar y aplicar". El porcentaje se aplica tanto a los precios por servicio como a las excepciones por plato.',
    },
    {
      question: '¿Por qué no puedo activar un contrato que ya tiene una excepción por plato cargada?',
      answer:
        'Una excepción por plato sola no alcanza: la activación exige al menos un precio por servicio vigente. Cargá el precio por servicio correspondiente antes de activar.',
    },
    {
      question: '¿Qué es el concepto de facturación de un precio por servicio?',
      answer:
        'Es el concepto, del catálogo de Configuración → Conceptos de Facturación, bajo el cual se factura ese servicio. Es obligatorio y se ve en la columna "Concepto" de la lista de precios. Un contrato puede usar conceptos distintos para almuerzo y cena; en ese caso las facturas que incluyan ambos viajan a ARCA como productos y servicios. Solo se pueden elegir conceptos activos.',
    },
    {
      question: '¿Puedo cambiar el punto de venta de un contrato?',
      answer:
        'Solo mientras el contrato está en borrador. Una vez activado, el punto queda fijo para no mezclar la numeración ni la producción. Si la empresa pasa a atenderse desde otro punto, dá de baja el contrato y creá uno nuevo en el punto correcto. En la lista de contratos podés filtrar por "Punto de venta".',
    },
    {
      question: '¿Puedo subir cualquier tipo de archivo como documento del contrato?',
      answer: 'No, solo se aceptan archivos PDF de hasta 5 MB.',
    },
    {
      question: '¿Un contrato vencido siempre tiene un sucesor?',
      answer:
        'No necesariamente. Si nadie lo reemplazó, el historial de versiones lo muestra como "Vencido sin sucesor".',
    },
  ],
  tips: [
    'Revisá la hora de corte efectiva del contrato antes de dar por cerrado un alta: afecta hasta cuándo se pueden confirmar pedidos.',
    'Cargá el precio por servicio de almuerzo y de cena apenas des de alta al contrato: sin eso no vas a poder activarlo.',
    'Reservá la excepción por plato para casos puntuales, no para reemplazar el precio por servicio: si todos los platos de un tipo necesitan el mismo precio nuevo, editá el precio por servicio en vez de cargar una excepción por cada uno.',
    'Usá "Previsualizar" siempre antes de aplicar una actualización masiva de precios, incluso si estás seguro del porcentaje.',
    'Cargá el documento firmado apenas lo tengas: evita tener que volver a buscarlo más adelante.',
    'Si necesitás dar de baja un contrato, escribí un motivo específico: queda en el historial y ayuda a auditorías futuras.',
  ],
};
